import * as THREE from 'three';
import React, { useRef, useState, useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { Interactive } from '@react-three/xr';
import { sendOnLoadMetric, sendOnClickMetric, fetchCampaignAd, AD_REFRESH_INTERVAL } from '../../utils/networking';
import { formats, defaultFormat, defaultStyle } from '../../utils/formats';
import { openURL, visibilityCheck } from '../../utils/helpers';

export * from '../../utils/formats';
import { version } from '../package.json';

console.log('Zesty SDK Version: ', version);

let interval = null;

export default function ZestyBanner(props) {
  const [bannerData, setBannerData] = useState(false);
  const [material, setMaterial] = useState(new THREE.MeshBasicMaterial());
  const [refreshInterval, setRefreshInterval] = useState(null);
  const { scene, gl } = useThree();
  const mesh = useRef();

  const adUnit = props.adUnit;
  const format = props.format ?? defaultFormat;

  const width = props.width ?? formats[format].width;
  const height = props.height ?? formats[format].height;

  const newStyle = props.style ?? defaultStyle;
  const beacon = props.beacon ?? true;

  const loadBanner = async (adUnit, format, style) => {
    const activeCampaign = await fetchCampaignAd(adUnit, format, style);
    const { asset_url, cta_url } = activeCampaign.Ads[0];
    return { asset_url, cta_url, campaignId: activeCampaign.CampaignId }
  };

  useEffect(() => {
    loadBanner(adUnit, format, newStyle).then((data) => {
      if (beacon) sendOnLoadMetric(adUnit, data.campaignId);
      setBannerData({ image: data.asset_url, url: data.cta_url, campaignId: data.campaignId });
      mesh.current.url = data.cta_url;
    });
  }, [adUnit]);

  useEffect(() => {
    if (bannerData) {
      new THREE.TextureLoader().load(bannerData.image, tex => {
        const material = new THREE.MeshBasicMaterial({ map: tex, transparent: true });
        setMaterial(material);
      });
    }
  }, [bannerData]);

  const onClick = (event) => {
    const banner = bannerData;
    let url = banner.url || banner.properties?.url;
    if (gl.xr.isPresenting) {
      const session = gl.xr.getSession();
      if (session) session.end();
    }
    openURL(url);
    if (props.beacon) sendOnClickMetric(props.adUnit, bannerData.campaignId);
  };

  const getCamera = () => {
    /** @type {THREE.Camera} */
    let camera = null;

    // Get the origin of the camera
    if (gl.xr.isPresenting) {
      camera = gl.xr.getCamera();
    } else {
      camera = scene.getObjectByProperty('isCamera', true);
    }

    return camera;
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const camera = getCamera();
      mesh.current.geometry.computeBoundingBox();
      const bb = new THREE.Box3().setFromObject(mesh.current);
      const isVisible = visibilityCheck(
        bb.min.toArray(),
        bb.max.toArray(),
        camera.projectionMatrix.toArray(),
        camera.matrixWorld.toArray(),
      );
      if (isVisible) {
        loadBanner(adUnit, format, newStyle).then(banner => {
          setBannerData({ image: banner.asset_url, url: banner.cta_url, campaignId: banner.campaignId });
        });
      }
    }, AD_REFRESH_INTERVAL);
    setRefreshInterval(interval);
  }, []);

  return (
    <Interactive onSelect={onClick}>
      <mesh {...props} ref={mesh} scale={0.5} onClick={onClick} material={material}>
        <planeGeometry
          args={[formats[format].width * height, height]}
        />
      </mesh>
    </Interactive>
  );
}
